import { useState, useEffect } from "react";

export interface LocalAgent {
  id: string;
  name: string;
  description: string;
  prompt: string;
  model: string;
  created_at: string;
  version: number;
  isLocal: true;
  source_type?: "local" | "langfuse"; // Track if edited from Langfuse
  source_id?: string; // Original Langfuse agent name
}

export interface LocalAgentVersion {
  version: number;
  name: string;
  description: string;
  prompt: string;
  model: string;
  created_at: string;
}

const LOCAL_AGENTS_KEY = "playground_local_agents";
const LOCAL_AGENT_VERSIONS_KEY = "playground_local_agent_versions";

export const useLocalAgents = () => {
  const [localAgents, setLocalAgents] = useState<LocalAgent[]>([]);
  const [agentVersions, setAgentVersions] = useState<
    Record<string, LocalAgentVersion[]>
  >({});

  // Load agents from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_AGENTS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as LocalAgent[];
        // Migrate old agents without version to version 1
        const migratedAgents = parsed.map((agent) => ({
          ...agent,
          version: agent.version || 1,
        }));
        setLocalAgents(migratedAgents);
      } catch (error) {
        console.error("Failed to parse local agents:", error);
      }
    }

    const storedVersions = localStorage.getItem(LOCAL_AGENT_VERSIONS_KEY);
    if (storedVersions) {
      try {
        const parsed = JSON.parse(storedVersions);
        setAgentVersions(parsed);
      } catch (error) {
        console.error("Failed to parse local agent versions:", error);
      }
    }
  }, []);

  // Save agents to localStorage whenever they change
  useEffect(() => {
    if (localAgents.length > 0) {
      localStorage.setItem(LOCAL_AGENTS_KEY, JSON.stringify(localAgents));
    }
  }, [localAgents]);

  // Save version history to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(agentVersions).length > 0) {
      localStorage.setItem(
        LOCAL_AGENT_VERSIONS_KEY,
        JSON.stringify(agentVersions),
      );
    }
  }, [agentVersions]);

  const createAgent = (
    agentData: Omit<LocalAgent, "id" | "created_at" | "isLocal" | "version">,
  ) => {
    const agentId =
      agentData.source_type === "langfuse" && agentData.source_id
        ? `langfuse-${agentData.source_id}`
        : `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = new Date().toISOString();

    const newAgent: LocalAgent = {
      ...agentData,
      id: agentId,
      created_at: timestamp,
      version: 1,
      isLocal: true,
      source_type: agentData.source_type || "local",
      source_id: agentData.source_id,
    };

    // Store initial version in version history
    const initialVersion: LocalAgentVersion = {
      version: 1,
      name: agentData.name,
      description: agentData.description,
      prompt: agentData.prompt,
      model: agentData.model,
      created_at: timestamp,
    };

    setAgentVersions((prev) => ({
      ...prev,
      [agentId]: [initialVersion],
    }));

    setLocalAgents((prev) => [...prev, newAgent]);
    return newAgent;
  };

  const deleteAgent = (agentId: string) => {
    setLocalAgents((prev) => prev.filter((agent) => agent.id !== agentId));
    // Also delete version history
    setAgentVersions((prev) => {
      const newVersions = { ...prev };
      delete newVersions[agentId];
      return newVersions;
    });
  };

  const updateAgent = (
    agentId: string,
    updates: Partial<
      Omit<LocalAgent, "id" | "created_at" | "isLocal" | "version">
    >,
  ) => {
    setLocalAgents((prev) =>
      prev.map((agent) => {
        if (agent.id === agentId) {
          const newVersion = agent.version + 1;
          const updatedAgent = { ...agent, ...updates, version: newVersion };

          // Store new version in version history
          const newVersionEntry: LocalAgentVersion = {
            version: newVersion,
            name: updatedAgent.name,
            description: updatedAgent.description,
            prompt: updatedAgent.prompt,
            model: updatedAgent.model,
            created_at: new Date().toISOString(),
          };

          setAgentVersions((prevVersions) => ({
            ...prevVersions,
            [agentId]: [...(prevVersions[agentId] || []), newVersionEntry],
          }));

          return updatedAgent;
        }
        return agent;
      }),
    );
  };

  const getAgentById = (agentId: string) => {
    return localAgents.find((agent) => agent.id === agentId);
  };

  const getAgentVersions = (agentId: string): LocalAgentVersion[] => {
    return agentVersions[agentId] || [];
  };

  const getAgentVersion = (
    agentId: string,
    version: number,
  ): LocalAgentVersion | undefined => {
    const versions = agentVersions[agentId] || [];
    return versions.find((v) => v.version === version);
  };

  const restoreAgentVersion = (agentId: string, version: number) => {
    const versionToRestore = getAgentVersion(agentId, version);
    if (!versionToRestore) {
      throw new Error(`Version ${version} not found for agent ${agentId}`);
    }

    // Update agent with version data (this will create a new version)
    updateAgent(agentId, {
      name: versionToRestore.name,
      description: versionToRestore.description,
      prompt: versionToRestore.prompt,
      model: versionToRestore.model,
    });
  };

  return {
    localAgents,
    createAgent,
    deleteAgent,
    updateAgent,
    getAgentById,
    getAgentVersions,
    getAgentVersion,
    restoreAgentVersion,
  };
};
